import type { Entity } from './entity';
import type { EmptyPropsPub } from './entityBase';
import type { EntityRepositoryBase } from './entityRepositoryBase';
import type { Factory } from './factory';
import type { FactoryBase } from './factoryBase';
import type { DeleteOutputType, EntitySubscribeType } from '@/infra/appsync';
import type { Logger } from '@/infra/logger';
import type { EmptyJsonPub, EntityDataBaseType } from '@/types/common/entityBase';
import type { EntityDataPubNotNullType } from '@/types/common/entityPub';

/**
 * ドメインサービスのベース
 */
export abstract class DomainServiceBase {
  /** ロガー */
  protected logger: Logger;

  /**
   * コンストラクタ
   * @param logger ロガー
   */
  public constructor(logger: Logger) {
    this.logger = logger;
  }

  private convertToJson<P, J, PP, JP>(
    input: EntitySubscribeType,
    factory: FactoryBase<EntityRepositoryBase<P, J, PP, JP>, P, J, PP, JP>,
  ): EntityDataBaseType<J, JP> {
    const entity = factory.createFromEntityType({ data: input.data, pub: input.pub });
    return entity.toJson();
  }

  /**
   * Subscriptionした値をJsonに変換する(Pvt系Entity用)
   * @param input Subscriptionの値
   * @param factory Factory
   * @returns json
   */
  public convertToJsonPvt<P, J>(
    input: EntitySubscribeType,
    factory: Factory<Entity<P, J>, P, J>,
  ): J {
    const item = this.convertToJson<P, J, EmptyPropsPub, EmptyJsonPub>(input, factory);
    return item.data;
  }

  /**
   * Subscriptionした値をJsonに変換する(Pub系Entity用)
   * @param input Subscriptionの値
   * @param factory Factory
   * @returns json
   */
  public convertToJsonPub<P, J, PP, JP>(
    input: EntitySubscribeType,
    factory: FactoryBase<EntityRepositoryBase<P, J, PP, JP>, P, J, PP, JP>,
  ): EntityDataPubNotNullType<J, JP> {
    const item = this.convertToJson<P, J, PP, JP>(input, factory);
    if (item.pub) {
      return {
        data: item.data,
        pub: item.pub,
      };
    }
    throw new Error(`pubがありません:convertToJsonPub:${JSON.stringify(item)}`);
  }

  /**
   * Jsonリストにinputを追加します.
   * @param jsonList jsonリスト
   * @param input 入力
   * @param factory ファクトリ
   * @param filter フィルタ.falseなら追加しない
   * @returns 新しいJsonリスト
   */
  private createEntityStore<P, J, PP, JP>(
    jsonList: EntityDataBaseType<J, JP>[],
    input: EntitySubscribeType,
    factory: FactoryBase<EntityRepositoryBase<P, J, PP, JP>, P, J, PP, JP>,
    isAdd: boolean,
  ): EntityDataBaseType<J, JP>[] {
    if (isAdd) {
      const entity = factory.createFromEntityType({ data: input.data, pub: input.pub });
      const newJsonList = [];
      let isExist = false;
      for (let i = 0; i < jsonList.length; i += 1) {
        const entityJson = factory.createFromJson(jsonList[i]);
        if (entity.isEqual(entityJson)) {
          isExist = true;
          newJsonList.push(entity.toJson());
        } else {
          newJsonList.push(jsonList[i]);
        }
      }
      if (!isExist) {
        newJsonList.push(entity.toJson());
      }
      return newJsonList.sort(factory.compare);
    }
    this.logger.warn(`Skip subscription data: createEntityStore: ${JSON.stringify(input)}`);
    const newJsonList = [];
    for (let i = 0; i < jsonList.length; i += 1) {
      newJsonList.push(jsonList[i]);
    }
    return newJsonList.sort(factory.compare);
  }

  /**
   * サブスクリプションの値でリストを更新する
   * @param jsonList Jsonリスト
   * @param input 入力
   * @param factory ファクトリ
   * @returns 更新Jsonリスト
   */
  public createEntityStorePvt<P, J>(
    jsonList: J[],
    input: EntitySubscribeType,
    factory: Factory<Entity<P, J>, P, J>,
    filter: ((json: J) => boolean) | undefined,
  ): J[] {
    const entity = factory.createFromEntityType(input);
    const isAdd = filter ? filter(entity.toJsonData()) : true;
    const items = this.createEntityStore<P, J, EmptyPropsPub, EmptyJsonPub>(
      jsonList.map((v) => ({ data: v, pub: null })),
      input,
      factory,
      isAdd,
    ).map((v) => v.data);
    return items.sort(factory.comparePvt);
  }

  /**
   * 現状の値と入力値から更新値を返す.
   * @param current 現状の値
   * @param input 入力値
   * @param factory ファクトリ
   * @returns 結果
   */
  private createEntityItemStore<P, J, PP, JP>(
    current: EntityDataBaseType<J, JP>,
    input: EntitySubscribeType,
    factory: FactoryBase<EntityRepositoryBase<P, J, PP, JP>, P, J, PP, JP>,
  ): EntityDataBaseType<J, JP> {
    const entity = factory.createFromEntityType({ data: input.data, pub: input.pub });
    const entityCurrent = factory.createFromJson(current);
    if (entity.isEqual(entityCurrent)) {
      return entity.toJson();
    }
    return entityCurrent.toJson();
  }

  /**
   * サブスクリプションのデータでリストを更新する
   * @param jsonList Jsonリスト
   * @param input 入力
   * @param factory ファクトリ
   * @returns 更新Jsonリスト
   */
  public createEntityStorePub<P, J, PP, JP>(
    jsonList: EntityDataBaseType<J, JP>[],
    input: EntitySubscribeType,
    factory: FactoryBase<EntityRepositoryBase<P, J, PP, JP>, P, J, PP, JP>,
    filter: ((json: J) => boolean) | undefined,
  ): EntityDataPubNotNullType<J, JP>[] {
    const entity = factory.createFromEntityType(input);
    const isAdd = filter ? filter(entity.toJsonData()) : true;
    const items = this.createEntityStore<P, J, PP, JP>(
      jsonList,
      input,
      factory,
      isAdd,
    );
    return items.map((v) => {
      if (v.pub) {
        return {
          data: v.data,
          pub: v.pub,
        };
      }
      throw new Error(`pubがありません:createEntityStorePub:${JSON.stringify(v)}`);
    });
  }

  /**
   * 現状の値と入力値から更新値を返す.
   * @param current 現状の値
   * @param input 入力値
   * @param factory ファクトリ
   * @returns 結果
   */
  public createEntityItemStorePvt<P, J>(
    current: J,
    input: EntitySubscribeType,
    factory: Factory<Entity<P, J>, P, J>,
  ): J {
    return this.createEntityItemStore<P, J, EmptyPropsPub, EmptyJsonPub>(
      { data: current, pub: null },
      input,
      factory,
    ).data;
  }

  /**
   * Jsonリストをinputで更新します.
   * @param jsonList jsonリスト
   * @param input 入力
   * @param factory ファクトリ
   * @returns 新しいJsonリスト
   */
  private updateEntityStore<P, J, PP, JP>(
    jsonList: EntityDataBaseType<J, JP>[],
    input: EntitySubscribeType,
    factory: FactoryBase<EntityRepositoryBase<P, J, PP, JP>, P, J, PP, JP>,
  ): EntityDataBaseType<J, JP>[] {
    const entity = factory.createFromEntityType({ data: input.data, pub: input.pub });
    const newJsonList = [];
    for (let i = 0; i < jsonList.length; i += 1) {
      const entityJson = factory.createFromJson(jsonList[i]);
      if (entity.isEqual(entityJson)) {
        newJsonList.push(entity.toJson());
      } else {
        newJsonList.push(jsonList[i]);
      }
    }
    return newJsonList;
  }

  /**
   * サブスクリプションの値でリストを更新する
   * @param jsonList Jsonリスト
   * @param input 入力
   * @param factory ファクトリ
   * @returns 更新Jsonリスト
   */
  public updateEntityStorePub<P, J, PP, JP>(
    jsonList: EntityDataBaseType<J, JP>[],
    input: EntitySubscribeType,
    factory: FactoryBase<EntityRepositoryBase<P, J, PP, JP>, P, J, PP, JP>,
  ): EntityDataPubNotNullType<J, JP>[] {
    const items = this.updateEntityStore<P, J, PP, JP>(jsonList, input, factory);
    return items.map((v) => {
      if (v.pub) {
        return {
          data: v.data,
          pub: v.pub,
        };
      }
      throw new Error(`pubがありません:createEntityStorePub:${JSON.stringify(v)}`);
    });
  }

  /**
   * サブスクリプションの値でリストを更新する
   * @param jsonList Jsonリスト
   * @param input 入力
   * @param factory ファクトリ
   * @returns 更新Jsonリスト
   */
  public updateEntityStorePvt<P, J>(
    jsonList: J[],
    input: EntitySubscribeType,
    factory: Factory<Entity<P, J>, P, J>,
  ): J[] {
    return this.updateEntityStore<P, J, EmptyPropsPub, EmptyJsonPub>(
      jsonList.map((v) => ({ data: v, pub: null })),
      input,
      factory,
    ).map((v) => v.data);
  }

  /**
   * 現状の値と入力値から更新値を返す.
   * @param current 現状の値
   * @param input 入力値
   * @param factory ファクトリ
   * @returns 結果
   */
  private updateEntityItemStore<P, J, PP, JP>(
    current: EntityDataBaseType<J, JP>,
    input: EntitySubscribeType,
    factory: FactoryBase<EntityRepositoryBase<P, J, PP, JP>, P, J, PP, JP>,
  ): EntityDataBaseType<J, JP> {
    const entity = factory.createFromEntityType({ data: input.data, pub: input.pub });
    const entityCurrent = factory.createFromJson(current);
    if (entity.isEqual(entityCurrent)) {
      return entity.toJson();
    }
    return entityCurrent.toJson();
  }

  /**
   * 現状の値と入力値から更新値を返す.
   * @param current 現状の値
   * @param input 入力値
   * @param factory ファクトリ
   * @returns 結果
   */
  public updateEntityItemStorePvt<P, J>(
    current: J,
    input: EntitySubscribeType,
    factory: Factory<Entity<P, J>, P, J>,
  ): J {
    return this.updateEntityItemStore<P, J, EmptyPropsPub, EmptyJsonPub>(
      { data: current, pub: null },
      input,
      factory,
    ).data;
  }

  /**
   * Jsonリストからinputを削除します.
   * @param jsonList jsonリスト
   * @param input 入力
   * @param factory ファクトリ
   * @returns 新しいJsonリスト
   */
  private deleteEntityStore<P, J, PP, JP>(
    jsonList: EntityDataBaseType<J, JP>[],
    input: DeleteOutputType,
    factory: FactoryBase<EntityRepositoryBase<P, J, PP, JP>, P, J, PP, JP>,
  ): EntityDataBaseType<J, JP>[] {
    const newJsonList = [];
    for (let i = 0; i < jsonList.length; i += 1) {
      const entityJson = factory.createFromJson(jsonList[i]);
      if (!entityJson.isEqualKey(input.key)) {
        newJsonList.push(jsonList[i]);
      }
    }
    return newJsonList;
  }

  /**
   * サブスクリプションの値でリストを更新する
   * @param jsonList Jsonリスト
   * @param input 入力
   * @param factory ファクトリ
   * @returns 更新Jsonリスト
   */
  public deleteEntityStorePub<P, J, PP, JP>(
    jsonList: EntityDataBaseType<J, JP>[],
    input: DeleteOutputType,
    factory: FactoryBase<EntityRepositoryBase<P, J, PP, JP>, P, J, PP, JP>,
  ): EntityDataPubNotNullType<J, JP>[] {
    const items = this.deleteEntityStore<P, J, PP, JP>(jsonList, input, factory);
    return items.map((v) => {
      if (v.pub) {
        return {
          data: v.data,
          pub: v.pub,
        };
      }
      throw new Error(`pubがありません:createEntityStorePub:${JSON.stringify(v)}`);
    });
  }

  /**
   * 現状の値と入力値から更新値を返す.
   * @param jsonList 現状の値
   * @param input 入力値
   * @param factory ファクトリ
   * @returns 結果
   */
  public deleteEntityStorePvt<P, J>(
    jsonList: J[],
    input: DeleteOutputType,
    factory: Factory<Entity<P, J>, P, J>,
  ): J[] {
    return this.deleteEntityStore<P, J, EmptyPropsPub, EmptyJsonPub>(
      jsonList.map((v) => ({ data: v, pub: null })),
      input,
      factory,
    ).map((v) => v.data);
  }

  /**
   * 現状の値と入力値から更新値を返す.
   * @param current 現状の値
   * @param input 入力値
   * @param factory ファクトリ
   * @returns 結果
   */
  private deleteEntityItemStore<P, J, PP, JP>(
    current: EntityDataBaseType<J, JP>,
    input: DeleteOutputType,
    factory: FactoryBase<EntityRepositoryBase<P, J, PP, JP>, P, J, PP, JP>,
  ): EntityDataBaseType<J, JP> | undefined {
    const entityJson = factory.createFromJson(current);
    if (entityJson.isEqualKey(input.key)) {
      return undefined;
    }
    return current;
  }

  /**
   * サブスクリプションの値で値を更新する
   * @param current 現在値
   * @param input 入力
   * @param factory ファクトリ
   * @returns 更新値
   */
  public deleteEntityItemStorePvt<P, J>(
    current: J,
    input: DeleteOutputType,
    factory: Factory<Entity<P, J>, P, J>,
  ): J | undefined {
    const val = this.deleteEntityItemStore<P, J, EmptyPropsPub, EmptyJsonPub>(
      { data: current, pub: null },
      input,
      factory,
    );
    if (val) {
      return val.data;
    }
    return undefined;
  }
}
